/**
 * AllocationsReview -- Lead-arranger order book + allocation issuance.
 *
 * Sprint C-LSP-02 Wave C-FE.
 *
 * The lead-side counterpart to the participant Indications page. Lists all
 * Indications of Interest submitted against an offering, lets the lead set an allocated
 * amount per row, and issues allocations via
 * POST /api/v1/syndication/iois/{ioiId}/allocate. After allocation the
 * IOI transitions to ALLOCATION_OFFERED and the participant must accept
 * or decline before the allocation expires.
 *
 * Route: /syndication/offerings/:offeringId/allocations (lead-only).
 *
 * The page does not gate at the route level; the LSP backend's role
 * check is the source of truth. A non-lead caller hits 403 from the
 * allocate endpoint and the UI surfaces the error inline.
 */

import { useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { AlertCircle, ArrowLeft } from 'lucide-react';
import {
  Card,
  Badge,
  Button,
  MetricCard,
  MetricCardGrid,
  Skeleton,
  Input,
  DataGrid,
} from '@aaimplatform/ui';
import type { ColumnDef } from '@aaimplatform/ui';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { components as LspComponents } from '@aaimplatform/institutional-types/loan-syndication-protocol';
import { loanSyndicationClient } from '../../api/client';
import { formatCurrencyFull, formatCurrencyCompact as formatCurrency } from './format';

type ParticipationIoi = LspComponents['schemas']['ParticipationIoi'];

function getIoiStatusVariant(
  status: ParticipationIoi['status'],
): 'success' | 'warning' | 'info' | 'neutral' | 'error' {
  switch (status) {
    case 'SUBMITTED':
      return 'info';
    case 'ALLOCATION_OFFERED':
      return 'warning';
    case 'ACCEPTED':
      return 'success';
    case 'DECLINED':
      return 'neutral';
    case 'WITHDRAWN':
      return 'neutral';
    case 'EXPIRED':
      return 'error';
    default:
      return 'neutral';
  }
}

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

function useOffering(offeringId: string | undefined) {
  return useQuery({
    queryKey: ['lsp-offering', offeringId],
    queryFn: async () => {
      if (!offeringId) throw new Error('offeringId is required');
      const { data, error } = await loanSyndicationClient.GET(
        '/api/v1/syndication/offerings/{offeringId}',
        { params: { path: { offeringId } } },
      );
      if (error) {
        throw new Error(
          (error as { message?: string })?.message ?? 'Failed to load offering',
        );
      }
      return data;
    },
    enabled: !!offeringId,
    staleTime: 30 * 1000,
    retry: false,
  });
}

function useOfferingIois(offeringId: string | undefined) {
  return useQuery({
    queryKey: ['lsp-offering-iois', offeringId],
    queryFn: async () => {
      if (!offeringId) throw new Error('offeringId is required');
      const { data, error } = await loanSyndicationClient.GET(
        '/api/v1/syndication/offerings/{offeringId}/iois',
        { params: { path: { offeringId } } },
      );
      if (error) {
        throw new Error(
          (error as { message?: string })?.message ?? 'Failed to load indications of interest',
        );
      }
      return data;
    },
    enabled: !!offeringId,
    staleTime: 30 * 1000,
    retry: false,
  });
}

function useAllocateIoi() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { ioiId: string; allocatedAmount: string }) => {
      const { data, error } = await loanSyndicationClient.POST(
        '/api/v1/syndication/iois/{ioiId}/allocate',
        {
          params: { path: { ioiId: vars.ioiId } },
          body: { allocatedAmount: vars.allocatedAmount },
        },
      );
      if (error) {
        throw new Error(
          (error as { message?: string })?.message ?? 'Allocation failed',
        );
      }
      return data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['lsp-offering-iois'] });
      void queryClient.invalidateQueries({ queryKey: ['lsp-mine-iois'] });
    },
  });
}

// ---------------------------------------------------------------------------
// Allocation row -- inline allocated-amount editor
// ---------------------------------------------------------------------------

function AllocationRowActions({ ioi }: { ioi: ParticipationIoi }) {
  const [amount, setAmount] = useState<string>(
    ioi.indicatedAmount !== undefined ? String(ioi.indicatedAmount) : '',
  );
  const mutation = useAllocateIoi();

  const numeric = Number(amount);
  const validNumeric =
    Number.isFinite(numeric) && numeric > 0 && numeric <= ioi.indicatedAmount;
  const canIssue =
    ioi.status === 'SUBMITTED' && validNumeric && !mutation.isPending;

  function handleIssue() {
    mutation.mutate({
      ioiId: ioi.ioiId,
      // Wire-precision contract: AllocateIoiRequest.allocatedAmount is a
      // string decimal. Convert via String() at the call site so the
      // Input value (already a string) is forwarded as-is when valid.
      allocatedAmount: amount,
    });
  }

  return (
    <div className="flex items-center gap-2">
      <Input
        type="number"
        value={amount}
        onChange={(e) => setAmount(e.target.value)}
        min={0}
        max={ioi.indicatedAmount}
        step={1000}
        disabled={ioi.status !== 'SUBMITTED' || mutation.isPending}
        className="w-32"
      />
      <Button
        variant="primary"
        size="sm"
        onClick={handleIssue}
        disabled={!canIssue}
      >
        {mutation.isPending ? 'Issuing...' : 'Issue Allocation'}
      </Button>
      {mutation.isError && (
        <span className="text-xs font-sans text-error">
          {mutation.error instanceof Error
            ? mutation.error.message
            : 'Allocation failed'}
        </span>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export function AllocationsReview() {
  const { offeringId } = useParams<{ offeringId: string }>();
  const navigate = useNavigate();

  const {
    data: offering,
    isLoading: offeringLoading,
    error: offeringError,
  } = useOffering(offeringId);
  const {
    data: ioiList,
    isLoading: ioisLoading,
    error: ioisError,
  } = useOfferingIois(offeringId);

  const iois: ParticipationIoi[] = useMemo(
    () => ioiList?.iois ?? [],
    [ioiList],
  );

  const totalIndicated = useMemo(
    () => iois.reduce((sum, i) => sum + (i.indicatedAmount ?? 0), 0),
    [iois],
  );
  const totalAllocated = useMemo(
    () =>
      iois.reduce(
        (sum, i) =>
          i.status === 'ALLOCATION_OFFERED' ||
          i.status === 'ACCEPTED'
            ? sum + (i.allocatedAmount ?? 0)
            : sum,
        0,
      ),
    [iois],
  );
  const remainingCapacity = useMemo(() => {
    const total =
      offering?.participationStructure.totalSyndicationAmount ?? 0;
    return Math.max(total - totalAllocated, 0);
  }, [offering, totalAllocated]);

  const columns = useMemo<ColumnDef<ParticipationIoi, unknown>[]>(
    () => [
      {
        id: 'institution',
        header: 'Participant',
        cell: ({ row }) => (
          <div>
            <div className="text-sm font-sans font-medium text-primary leading-normal">
              {row.original.institutionName ?? 'Indicated participant'}
            </div>
            <div className="text-xs font-mono text-tertiary tabular-nums leading-normal">
              {row.original.participantId.slice(0, 8)}...
            </div>
          </div>
        ),
        size: 240,
      },
      {
        accessorKey: 'indicatedAmount',
        header: 'Indicated',
        cell: ({ getValue }) => (
          <span className="text-sm font-mono text-primary tabular-nums leading-normal">
            {formatCurrencyFull(getValue() as number)}
          </span>
        ),
        size: 160,
      },
      {
        accessorKey: 'notes',
        header: 'Notes',
        cell: ({ getValue }) => {
          const notes = getValue() as string | undefined;
          if (!notes) {
            return (
              <span className="text-sm font-sans text-tertiary leading-normal">
                --
              </span>
            );
          }
          return (
            <span
              className="text-sm font-sans text-secondary leading-normal"
              title={notes}
            >
              {notes.length > 60 ? `${notes.slice(0, 60)}...` : notes}
            </span>
          );
        },
        size: 240,
      },
      {
        accessorKey: 'allocatedAmount',
        header: 'Allocated',
        cell: ({ getValue }) => {
          const allocated = getValue() as number | undefined;
          return (
            <span className="text-sm font-mono text-primary tabular-nums leading-normal">
              {allocated !== undefined ? formatCurrencyFull(allocated) : '--'}
            </span>
          );
        },
        size: 160,
      },
      {
        accessorKey: 'status',
        header: 'Status',
        cell: ({ row }) => (
          <Badge variant={getIoiStatusVariant(row.original.status)} size="sm">
            {row.original.status}
          </Badge>
        ),
        size: 180,
      },
      {
        id: 'actions',
        header: 'Action',
        cell: ({ row }) => <AllocationRowActions ioi={row.original} />,
        size: 360,
      },
    ],
    [],
  );

  const isLoading = offeringLoading || ioisLoading;
  const errorMessage =
    (offeringError instanceof Error ? offeringError.message : null) ??
    (ioisError instanceof Error ? ioisError.message : null);

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-10 w-72" />
        <MetricCardGrid>
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-24" />
          ))}
        </MetricCardGrid>
        <Card className="p-6">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="h-12 w-full mb-2" />
          ))}
        </Card>
      </div>
    );
  }

  if (errorMessage || !offering) {
    return (
      <div className="bg-error/5 border border-error/20 rounded-sm p-4 flex items-center gap-3">
        <AlertCircle className="w-5 h-5 text-error flex-shrink-0" />
        <div className="text-sm font-sans text-error">
          Failed to load allocations review:{' '}
          {errorMessage ?? 'offering not found'}
        </div>
      </div>
    );
  }

  return (
    <div>
      <button
        type="button"
        className="inline-flex items-center gap-2 text-sm font-sans text-tertiary hover:text-primary transition-colors mb-3"
        onClick={() =>
          navigate(`/syndication/offerings/${offering.offeringId}`)
        }
      >
        <ArrowLeft className="w-4 h-4" />
        Back to Offering
      </button>

      <div className="flex items-center justify-between mb-6">
        <div>
          {/* Page title lives in the sticky topnav (AppHeader); the body opens
              with the descriptive subtitle, no redundant H1. */}
          <p className="text-sm font-sans text-tertiary">
            Lead-side IOI review and allocation issuance for{' '}
            <span className="font-mono tabular-nums">
              {offering.offeringId.slice(0, 8)}
            </span>
            ...
          </p>
        </div>
      </div>

      <MetricCardGrid className="mb-6">
        <MetricCard
          label="Total Indications of Interest"
          value={iois.length.toString()}
          change={`${iois.filter((i) => i.status === 'SUBMITTED').length} pending`}
          changeType="neutral"
        />
        <MetricCard
          label="Total Indicated"
          value={formatCurrency(totalIndicated)}
          change="Across all participants"
          changeType="neutral"
        />
        <MetricCard
          label="Allocated"
          value={formatCurrency(totalAllocated)}
          change={
            iois.filter((i) => i.status === 'ALLOCATION_OFFERED').length +
            ' offered, ' +
            iois.filter((i) => i.status === 'ACCEPTED').length +
            ' accepted'
          }
          changeType="neutral"
        />
        <MetricCard
          label="Remaining Capacity"
          value={formatCurrency(remainingCapacity)}
          change={`Total ${formatCurrency(offering.participationStructure.totalSyndicationAmount)}`}
          changeType="neutral"
        />
      </MetricCardGrid>

      {iois.length === 0 ? (
        <Card className="p-8 text-center">
          <div className="text-sm font-sans text-secondary">
            No indications of interest have been submitted on this offering.
          </div>
        </Card>
      ) : (
        <DataGrid<ParticipationIoi>
          data={iois}
          columns={columns}
          enablePagination
          defaultPageSize={20}
          enableColumnResize
          hoverable
          getRowId={(row) => row.ioiId}
          className="w-full rounded-sm shadow-sm"
        />
      )}
    </div>
  );
}
